/**
 * Utility functions for detecting URLs and fetching favicons for craftspace icons
 */

const VALID_DOMAIN_EXTENSIONS = [
  "com",
  "org",
  "net",
  "edu",
  "gov",
  "mil",
  "int",
  "co",
  "io",
  "ai",
  "tech",
  "app",
  "dev",
  "design",
  "studio",
  "agency",
  "digital",
  "online",
  "site",
  "website",
  "blog",
  "shop",
  "store",
  "biz",
  "info",
  "name",
  "pro",
  "tv",
  "me",
  "us",
  "uk",
  "ca",
  "au",
  "de",
  "fr",
  "jp",
  "cn",
  "ru",
  "br",
  "in",
  "it",
  "es",
  "nl",
  "pl",
  "se",
  "no",
  "dk",
  "fi",
  "cz",
  "at",
  "ch",
  "be",
];

/**
 * Detects if a given text looks like a domain/URL
 * @param text - The input text to check
 * @returns boolean indicating if it looks like a URL
 */
export const isUrl = (text: string): boolean => {
  // Remove any protocol if present
  const cleanText = text
    .replace(/^https?:\/\//, "")
    .toLowerCase()
    .trim();

  // Must contain at least one dot
  if (!cleanText.includes(".")) return false;

  // Split by dots and check if last part is a valid extension
  const parts = cleanText.split(".");
  if (parts.length < 2) return false;

  const extension = parts[parts.length - 1];

  // Check if it's a valid domain extension
  return VALID_DOMAIN_EXTENSIONS.includes(extension);
};

/**
 * Normalizes a domain name by ensuring it has the proper protocol
 * @param domain - The domain to normalize
 * @returns The normalized URL
 */
export const normalizeDomain = (domain: string): string => {
  const cleanDomain = domain.replace(/^https?:\/\//, "").trim();
  return `https://${cleanDomain}`;
};

/**
 * Tests if an image URL can be loaded
 * @param url - The image URL to test
 * @returns Promise<boolean> - Whether the image loads successfully
 */
const testImageLoad = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;

    // Timeout after 5 seconds
    setTimeout(() => resolve(false), 5000);
  });
};

/**
 * Gets favicon using Google's favicon service as a fallback
 * @param url - The URL to get favicon for
 * @returns string - The Google favicon service URL
 */
const getFaviconFromService = (url: string): string => {
  const domain = url.replace(/^https?:\/\//, "").split("/")[0];
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
};

/**
 * Attempts to fetch a favicon from a domain
 * @param domain - The domain to fetch favicon from
 * @returns Promise<string | null> - The favicon URL or null if not found
 */
export const fetchFavicon = async (domain: string): Promise<string | null> => {
  const normalizedUrl = normalizeDomain(domain);
  // console.log("Fetching favicon for normalized URL:", normalizedUrl);

  // List of common favicon paths to try
  const faviconPaths = [
    "/favicon.ico",
    "/favicon.png",
    "/favicon.svg",
    "/apple-touch-icon.png",
    "/apple-touch-icon-180x180.png",
    "/android-chrome-192x192.png",
  ];

  // Try each favicon path
  for (const path of faviconPaths) {
    try {
      const faviconUrl = `${normalizedUrl}${path}`;
      // console.log("Testing favicon URL:", faviconUrl);

      // Test if the favicon exists by trying to load it as an image
      const imageLoadResult = await testImageLoad(faviconUrl);
      // console.log("Image load result for", faviconUrl, ":", imageLoadResult);

      if (imageLoadResult) {
        // console.log("Found favicon at:", faviconUrl);
        return faviconUrl;
      }
    } catch (_error) {
      // console.log("Error testing favicon path", path, ":", _error);
    }
  }

  // Fall back to Google's favicon service
  // console.log("No direct favicon found, using Google service");
  const googleFavicon = getFaviconFromService(normalizedUrl);
  // console.log("Google favicon URL:", googleFavicon);
  return googleFavicon;
};

/**
 * Main function to get favicon for a craftspace name
 * @param craftspaceName - The craftspace name to check
 * @returns Promise<string | null> - The favicon URL or null if not a URL
 */
export const getCraftspaceFavicon = async (craftspaceName: string): Promise<string | null> => {
  if (!isUrl(craftspaceName)) {
    // console.log("Not a URL, skipping favicon fetch:", craftspaceName);
    return null;
  }

  console.log("Attempting to fetch favicon for:", craftspaceName);

  try {
    const result = await fetchFavicon(craftspaceName);
    console.log("Favicon fetch result:", result);
    return result;
  } catch (error) {
    console.warn("Failed to fetch favicon for craftspace:", craftspaceName, error);
    return null;
  }
};
